import React from 'react';
import { Link, Outlet } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { MultiAgentGateProvider, useMultiAgentGate } from '../hooks/useMultiAgentGate';
import { APP_NAME } from '../support/brand';
import { homePathFor, isAdmin, isUser } from '../support/accountFunction';
import { getUserNavSections } from '../support/iconMappings';
import BrandLogo from '../components/common/BrandLogo';
import AppNav from '../components/common/AppNav';
import MobileNav from '../components/common/MobileNav';
import MoreNavMenu from '../components/common/MoreNavMenu';
import HeaderUserMenu from '../components/common/HeaderUserMenu';

function AppLayoutContent() {
  const { user, account, memberships, currentMembership, logout } = useAuth();
  const { isMultiAgent } = useMultiAgentGate();
  const homePath = homePathFor(user, { memberships, account });
  const isUserLayout = isUser(user);
  const { primary, secondary } = isUserLayout
    ? getUserNavSections(user, { isMultiAgent, currentMembership })
    : { primary: [], secondary: [] };

  return (
    <div className="surface-page relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-forge-100/60 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-200/40 rounded-full blur-3xl" />
      </div>

      <header className="relative surface-header sticky top-0 z-40">
        <div
          className={`mx-auto px-4 h-14 flex items-center justify-between gap-3 ${
            isUserLayout ? 'max-w-6xl' : 'max-w-5xl'
          }`}
        >
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <Link to={homePath} className="shrink-0" aria-label={APP_NAME}>
              <BrandLogo className="h-9 w-9" />
            </Link>
            {isUserLayout ? (
              <div className="hidden sm:flex items-center gap-4 min-w-0">
                <AppNav items={primary} className="flex items-center gap-4 min-w-0" />
                <MoreNavMenu items={secondary} />
              </div>
            ) : (
              <AppNav user={user} isMultiAgent={isMultiAgent} currentMembership={currentMembership} />
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isUserLayout ? (
              <HeaderUserMenu />
            ) : (
              <button
                type="button"
                onClick={logout}
                className="hidden sm:inline-flex items-center gap-1.5 text-sm text-content-muted hover:text-content-primary transition-colors"
              >
                <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                Sair
              </button>
            )}
            <MobileNav
              user={user}
              isMultiAgent={isMultiAgent}
              onLogout={logout}
              currentMembership={currentMembership}
            />
          </div>
        </div>
      </header>

      <main className="relative max-w-4xl mx-auto px-4 py-8">
        <Outlet />
      </main>
    </div>
  );
}

function AppLayout() {
  return (
    <MultiAgentGateProvider>
      <AppLayoutContent />
    </MultiAgentGateProvider>
  );
}

export default AppLayout;
